/**
 * Iter 246.8 — Panneau Autorisations staff (parité UX avec MemberProfilePage).
 *
 * Affiche les permissions staff Blitzz (legacy DEFAULT_PERMISSIONS) groupées
 * par catégorie, avec switches + 3 presets staff (Coach, Comptable, Sous-traitant).
 *
 * Utilisé dans StaffProfilePage quand !isOwnProfile + isAdmin.
 *
 * Endpoints :
 * - GET  /admin/permissions/:user_id
 * - PUT  /admin/permissions/:user_id   (body: { permissions: { ... } })
 */
import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import {
  Save, Loader2, AlertCircle, Shield, Users, FolderCog, Receipt,
  Wallet, Banknote, BookOpen, FileText, Calendar, Wrench, Building2,
  Sparkles, Activity, Award, FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";

// Catalogue déclaratif (parité avec MEMBER_PERMISSIONS_CATALOG côté tenant).
// Chaque permission_key correspond à un flag réel dans DEFAULT_PERMISSIONS backend.
const STAFF_PERMISSIONS_CATALOG = [
  {
    group: "Clients & CRM",
    icon: Users,
    permissions: [
      { key: "manage_clients",     label: "Gérer les clients (créer/modifier)" },
      { key: "delete_clients",     label: "Supprimer les clients" },
      { key: "view_all_clients",   label: "Voir TOUS les clients (sans assignation)" },
      { key: "manage_services",    label: "Gérer les services facturés" },
      { key: "delete_services",    label: "Supprimer des services" },
    ],
  },
  {
    group: "Comptabilité & Finance",
    icon: Wallet,
    permissions: [
      { key: "manage_depenses",     label: "Saisir les dépenses" },
      { key: "manage_encaissements",label: "Saisir les encaissements" },
      { key: "access_tresorerie",   label: "Accès trésorerie / dashboard finance" },
      { key: "access_finances",     label: "Accès rapports financiers" },
      { key: "access_fec",          label: "Accès Fichier des Écritures Comptables (FEC)" },
      { key: "access_comptabilite", label: "Module Comptabilité (Pennylane, FEC, etc.)" },
    ],
  },
  {
    group: "Formation & Qualiopi",
    icon: BookOpen,
    permissions: [
      { key: "manage_formations",            label: "Gérer les formations" },
      { key: "delete_formations",            label: "Supprimer les formations" },
      { key: "manage_qualiopi",              label: "Gérer le référentiel Qualiopi" },
      { key: "manage_formation_inscriptions",label: "Gérer les inscriptions formation" },
      { key: "view_formation_bpf",           label: "Voir le BPF (Bilan Pédagogique)" },
      { key: "manage_formation_compliance",  label: "Gérer la conformité formation" },
      { key: "manage_evaluations_fin",       label: "Gérer les évaluations finales" },
    ],
  },
  {
    group: "Documents & Notes",
    icon: FileText,
    permissions: [
      { key: "manage_notes",     label: "Gérer les notes/commentaires" },
      { key: "delete_notes",     label: "Supprimer les notes" },
      { key: "manage_documents", label: "Téléverser/gérer les documents" },
      { key: "delete_documents", label: "Supprimer les documents" },
    ],
  },
  {
    group: "Activité & Agenda",
    icon: Calendar,
    permissions: [
      { key: "manage_agenda",       label: "Gérer l'agenda" },
      { key: "manage_audits",       label: "Gérer les audits" },
      { key: "delete_audits",       label: "Supprimer les audits" },
      { key: "manage_accompagnements", label: "Gérer les accompagnements" },
      { key: "delete_accompagnements", label: "Supprimer les accompagnements" },
      { key: "manage_equipements", label: "Gérer les équipements" },
    ],
  },
  {
    group: "Marketing & Communications",
    icon: Sparkles,
    permissions: [
      { key: "access_marketing",       label: "Accès au module Marketing" },
      { key: "access_workflows",       label: "Accès aux Workflows" },
      { key: "access_messaging_meta",  label: "Accès Messagerie Unifiée (Meta WA/Messenger/Insta)" },
      { key: "use_aircall_click_to_call", label: "Utiliser Aircall click-to-call" },
      { key: "use_ai_auto_qualify",    label: "Utiliser l'IA auto-qualification leads" },
    ],
  },
  {
    group: "Administration",
    icon: Shield,
    permissions: [
      { key: "manage_staff",       label: "Gérer l'équipe (inviter, supprimer)" },
      { key: "manage_permissions", label: "Gérer les permissions des autres staff" },
      { key: "manage_integrations",label: "Gérer les intégrations (API keys)" },
      { key: "manage_branding",    label: "Gérer le branding (logo, couleurs)" },
      { key: "access_builder",     label: "Accès au Builder (formations, sites)" },
      { key: "access_master_panel",label: "Accès Master Panel (Blitzz only)" },
    ],
  },
];

// 3 presets métier staff (parité avec ROLE_PRESETS tenant)
const STAFF_PRESETS = [
  {
    key: "coach",
    label: "Coach / Accompagnant",
    description: "Gère ses clients, accompagnements et formations. Pas d'accès admin/finances.",
    color: "#3b82f6",
    icon: Award,
    grants: [
      "manage_clients", "manage_services",
      "manage_accompagnements", "manage_formations",
      "manage_qualiopi", "manage_formation_inscriptions",
      "view_formation_bpf",
      "manage_audits", "manage_notes", "manage_documents",
      "manage_agenda", "use_aircall_click_to_call", "use_ai_auto_qualify",
    ],
  },
  {
    key: "comptable",
    label: "Comptable",
    description: "Accès complet finance + comptabilité. Lecture seule clients/formation.",
    color: "#a855f7",
    icon: Receipt,
    grants: [
      "view_all_clients",
      "manage_depenses", "manage_encaissements",
      "access_tresorerie", "access_finances", "access_fec",
      "access_comptabilite",
      "manage_documents", "manage_notes",
    ],
  },
  {
    key: "sous_traitant",
    label: "Sous-traitant",
    description: "Accès limité à ses clients assignés + saisie dépenses + documents.",
    color: "#10b981",
    icon: Wrench,
    grants: [
      "manage_clients", "manage_services",
      "manage_depenses",
      "manage_documents", "manage_notes",
      "manage_agenda", "manage_audits",
    ],
  },
];

export default function StaffPermissionsPanel({ userId, targetUser }) {
  const { axiosAuth, user: currentUser } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [perms, setPerms] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/permissions/${userId}`);
      setPerms(r.data.permissions || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de charger les permissions");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (userId) load(); /* eslint-disable-next-line */ }, [userId]);

  const togglePerm = (key) => {
    setPerms(p => ({ ...p, [key]: !p[key] }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/permissions/${userId}`, { permissions: perms });
      toast.success("Autorisations mises à jour");
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = (presetKey) => {
    const p = STAFF_PRESETS.find(x => x.key === presetKey);
    if (!p) return;
    if (!window.confirm(`Appliquer le preset "${p.label}" ?\n\nToutes les permissions actuelles seront remplacées par celles du preset.`)) return;
    // Build full perms dict : True only for granted keys, False elsewhere
    const grants = new Set(p.grants);
    const next = {};
    for (const group of STAFF_PERMISSIONS_CATALOG) {
      for (const perm of group.permissions) {
        next[perm.key] = grants.has(perm.key);
      }
    }
    setPerms(next);
    setDirty(true);
    toast.success(`Preset "${p.label}" prêt — cliquez Enregistrer pour appliquer`);
  };

  if (loading) {
    return (
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[#f7c935]" />
      </div>
    );
  }

  // Don't let admin edit their own permissions to avoid self-lockout
  const isSelf = userId === currentUser?.user_id;
  // Superadmins have full access by default — show panel but warn
  const isTargetSuperadmin = targetUser?.role === "superadmin";

  const enabledCount = Object.values(perms).filter(Boolean).length;
  const totalCount = STAFF_PERMISSIONS_CATALOG.reduce((sum, g) => sum + g.permissions.length, 0);

  return (
    <section className="bg-[#14101c] border border-white/5 rounded-xl p-5" data-testid="staff-permissions-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#f7c935]" /> Autorisations détaillées
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {isTargetSuperadmin
              ? "⚠️ Cet utilisateur est Superadmin — il a un accès complet par défaut, ces permissions sont indicatives."
              : `${enabledCount} permission(s) activée(s) sur ${totalCount}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <span className="flex items-center gap-1.5 text-xs text-amber-400">
              <AlertCircle className="w-3.5 h-3.5" /> Modifications non enregistrées
            </span>
          )}
          <Button
            onClick={save}
            disabled={!dirty || saving || isSelf}
            size="sm"
            className="bg-[#f7c935] hover:bg-[#e6b825] text-[#14101c]"
            data-testid="staff-perms-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            Enregistrer
          </Button>
        </div>
      </div>

      {isSelf && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4 text-xs text-amber-300">
          ⚠️ Vous ne pouvez pas modifier vos propres permissions (sécurité anti-lockout).
        </div>
      )}

      {/* Presets staff */}
      {!isSelf && !isTargetSuperadmin && (
        <div className="bg-black/30 border border-white/5 rounded-lg p-3 mb-4" data-testid="staff-presets-bar">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
            Appliquer un preset métier
          </div>
          <div className="flex flex-wrap gap-2">
            {STAFF_PRESETS.map((p) => {
              const Icon = p.icon;
              return (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p.key)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium hover:bg-white/5 transition-colors"
                  style={{ borderColor: `${p.color}40`, color: p.color }}
                  title={p.description}
                  data-testid={`staff-preset-${p.key}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {p.label}
                  <span className="opacity-50 ml-1">({p.grants.length})</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Groups */}
      <div className="space-y-4">
        {STAFF_PERMISSIONS_CATALOG.map((group) => {
          const Icon = group.icon;
          return (
            <div key={group.group} className="bg-black/20 rounded-lg p-4" data-testid={`staff-perm-group-${group.group}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded bg-[#f7c935]/15 text-[#f7c935] flex items-center justify-center">
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <h4 className="text-white font-bold text-sm">{group.group}</h4>
              </div>
              <div className="space-y-1.5">
                {group.permissions.map((p) => {
                  const val = !!perms[p.key];
                  return (
                    <div
                      key={p.key}
                      className="flex items-center justify-between gap-3 px-3 py-1.5 rounded hover:bg-white/[0.02]"
                      data-testid={`staff-perm-${p.key}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white">{p.label}</div>
                        <div className="text-[10px] text-slate-600 font-mono">{p.key}</div>
                      </div>
                      <Switch
                        checked={val}
                        disabled={isSelf}
                        onCheckedChange={() => !isSelf && togglePerm(p.key)}
                        data-testid={`staff-switch-${p.key}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
